<template>
  <layout name="Sidebar">
    <div class="support-form">
      <VLayoutHeader
        id="header"
        backBtn
        noLogo
        :label="_capitalizeFirstLetter(type)"
        :actions="actionButtons"
        extraClass="support-header"
      ></VLayoutHeader>
      <div class="support-form__form-wrapper">
        <h2 class="title">Provide {{ callType }} Details</h2>
        <VForm :name="name" :fields="fields" :extraClass="getFormClass"></VForm>
      </div></div
  ></layout>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import { mapActions, mapGetters } from "vuex";
import { required } from "@/utils/validations";
import { capitalizeFirstLetter } from "@/utils/util";

//configs
import {
  zendeskIssueResponsibleAgentId,
  zendeskQuestionResponsibleAgentId,
  zendeskIssueCcEmails,
  zendeskQuestionCcEmails
} from "@/config";

//components
import Layout from "@/layouts/Layout";
import VForm from "@/components/VForm";
import VLayoutHeader from "@/components/VLayoutHeader";

//mixins
import withFormUtils from "@/mixins/withFormUtils";
import withToasted from "@/mixins/withToasted";
import withNavigate from "@/mixins/withNavigate";

export default Vue.component("SupportForm", {
  components: {
    Layout,
    VForm,
    VLayoutHeader
  },

  mixins: [withFormUtils, withToasted, withNavigate],

  data() {
    return {
      name: "supportForm",
      id: "",
      email: "",
      company: "",
      contactName: "",
      type: this.$route.query.type,
      uploadFileTypes: "image/*",
      uploadedFiles: []
    };
  },

  computed: {
    callType() {
      return this.type === "question" ? "question" : "issue";
    },

    getFormClass() {
      return `support-form-wrapper ${this.callType}`;
    },

    fields() {
      const allFields = [
        {
          name: "subject",
          label: "Subject",
          classType: "primary",
          placeholder: `Put subject of ${this.callType} here`,
          extraClass: "support-subject",
          isRequired: true,
          validate: [required]
        },
        {
          name: "description",
          label: "Description",
          elementType: "textarea",
          placeholder: "Put your description here",
          isRequired: true,
          extraClass:
            this.type === "problem"
              ? "support-description problem"
              : "support-description question",
          validate: [required]
        },
        {
          name: "upload",
          label: "Image",
          uploaderType: "dragAndDrop",
          elementType: "uploader",
          isLoading: this.isLoading,
          onAddHandler: this._uploadAttachments,
          onRemoveHandler: this.deleteFileFromServer,
          extraClass: "support-upload",
          innerChildProps: {
            hint: {
              text: "Insert image(s) which describe the issue",
              size: "fit"
            }
          }
        }
      ];

      if (this.type === "problem") {
        /*Insert new fields into specify array position*/
        allFields.splice(
          2,
          0,
          {
            name: "url",
            label: "URL",
            placeholder: "Put issue URL",
            innerChildProps: {
              hint: {
                text: "Inkit App URL where the issue occurs",
                size: "fit"
              }
            }
          },
          {
            name: "video_url",
            label: "Video URL",
            placeholder: "Put video url here",
            innerChildProps: {
              hint: {
                text: "URL to video describing the issue",
                size: "fit"
              }
            }
          }
        );
      }
      return allFields;
    },

    ticketData() {
      return {
        request: {
          description: this.getValue("description"),
          requester: {
            email: this.email,
            name: `${this.contactName} (${this.company})`
          },
          type: this.type,
          subject: this.getValue("subject"),
          comment: {
            body: this.getValue("description"),
            uploads: this.uploadedFiles.map(item => item.token)
          },
          priority: this.getReportPriority,
          tags: [this.getTagByEnvUrl],
          custom_fields: [
            { id: 360043982993, value: this.id },
            { id: 360043982973, value: this.email },
            { id: 360044368414, value: this.getValue("url") },
            { id: 360044337813, value: this.getValue("video_url") },
            { id: 360043984033, value: "APP" },
            { id: 360047952214, value: `${this.contactName} (${this.company})` }
          ],
          assignee_id: +this.getAssigneeId,
          email_cc_ids: this.getEmailNotificationRecipients
        }
      };
    },

    actionButtons() {
      return [
        {
          label: "Send",
          value: "send",
          size: "regular",
          disabled: !this.isValid || this.isLoading,
          click: this.postNewTicket
        }
      ];
    },

    getReportPriority() {
      return this.type === "problem" ? "high" : "low";
    },

    getAssigneeId() {
      return this.type === "problem"
        ? zendeskIssueResponsibleAgentId
        : zendeskQuestionResponsibleAgentId;
    },

    getEmailNotificationRecipients() {
      return this.type === "problem"
        ? zendeskIssueCcEmails
        : zendeskQuestionCcEmails;
    },

    getTagByEnvUrl() {
      const currentUrl = window.location.href;
      const envMap = [
        { key: "local_development", value: "localhost" },
        { key: "development", value: "development" },
        { key: "staging", value: "staging" },
        { key: "production", value: "app.inkit.io" }
      ];
      return envMap.find(item => currentUrl.includes(item.value)).key;
    },

    ...mapGetters("account", ["identity"]),
    ...mapGetters("support", ["isLoading"])
  },

  methods: {
    postNewTicket() {
      this.validateForm();
      if (this.isValid) {
        const data = { ...this.ticketData };

        this.createTicket({
          data,
          onSuccess: response => {
            this.toast("The ticket created successfully!");
            this.goBack();
          },
          onError: () => {
            this.toast("Ticket creation failed", "error");
          }
        });
      }
    },

    _capitalizeFirstLetter(string) {
      return capitalizeFirstLetter(string);
    },

    _uploadAttachments(files) {
      let attachments = [];
      //add binary files
      for (const item of files) {
        attachments.push({ file: item, name: item.name });
      }

      const response = this.uploadAttachments({
        data: attachments,
        onSuccess: uploaded => {
          this.uploadedFiles.push(...uploaded);
          //   this.toast("Files attached successfully", "success", {
          //     icon: "check "
          //   });
          // },
          // onError: e => {
          //   this.toast("Files didn't upload.", "error", { duration: 4000 });
          // }
        }
      }).then(resp => Promise.resolve(resp.map(file => file.name)));

      return response;
      // return response.map(file => file.name);
    },

    deleteFileFromServer(key) {
      const fileToken = this.uploadedFiles.filter(file => file.name == key)[0]
        .token;
      console.log(fileToken);
      return this.deleteAttachment({
        data: fileToken,
        onSuccess: () => {
          this.uploadedFiles = this.uploadedFiles.filter(
            file => file.name !== key
          );
        }
        //
        // onError: e => {
        //   this.toast(e.message, "error", { duration: 4000 });
        // }
      }).then(() => Promise.resolve(key));
    },

    ...mapActions("support", [
      "createTicket",
      "uploadAttachments",
      "deleteAttachment",
      "updateRequest"
    ])
  },

  created() {
    const { user, account } = this.identity;

    this.id = user.id;
    this.email = user.email;
    this.company = account.company;
    this.contactName = `${user.first_name} ${user.last_name}`;
  },

  destroyed() {
    this.clearForm();
  }
});
</script>

<style lang="scss" scoped>
$ns: "support-form";

.#{$ns} {
  display: grid;
  grid-template-columns: max-content;
  grid-gap: 50px 80px;
  color: $secondary-text-color;
  padding-bottom: 30px;
  grid-template-columns: minmax(0, 970px);
}

.title {
  font-size: 16px;
  line-height: 1.2;
  margin-bottom: 30px;
}

.#{$ns}__uploader-wrapper {
  display: grid;
  grid-template-rows: min-content min-content;
  grid-gap: 30px;
  grid-template-columns: 400px;
}

.#{$ns}__actions {
  justify-self: right;
  grid-column-end: -1;
}
</style>

<style lang="scss">
.support-header {
  grid-column: 1/-1;
}

.support-form-wrapper {
  display: flex;
  flex-direction: column;
  max-height: 600px;

  &.question {
    max-height: 400px;
  }

  & > div {
    width: 470px;
  }

  &,
  &.question {
    @media only screen and (max-width: $medium-screen) {
      max-height: none;
      & > div {
        width: auto;
      }
    }
  }

  /* temporary fix for many images in uploader */
  .uploader {
    max-height: 300px;
  }
}

/* shloud be implement because of */
/* hint in second column */
.support-subject {
  margin-top: 3px;
}

.support-description {
  &.problem {
    .v-textarea__input-field {
      min-height: 380px;
    }
  }
  &.question {
    .v-textarea__input-field {
      min-height: 196px;
    }
  }
}

.support-upload {
  #drop-area {
    height: 285px;
  }
}
</style>
